import { useStanRouter } from '@/hooks/useStanRouter';
import { logWithSentry, MFA, removePrefixIfExists } from '@/lib';
import { handleApiError } from '@/lib/utils/apiErrorHandler';
import { useAuthStore } from '@/stores';
import { useCallback, useState } from 'react';
import { useMFARecoveryMutation, useMFAValidateOTPMutation } from '../queries';
import { mfaOTPSchema, mfaRecoverySchema } from '../validations';

export function useMFA(token: string, loginEndpoint: string, onSuccess?: () => void, onRecoverySuccess?: () => void) {
    const [error, setError] = useState<{
        status: string;
        message: string;
        errors: Record<string, string[]>;
    } | null>(null);
    const [warning, setWarning] = useState('');
    const { push } = useStanRouter();
    const saveToken = useAuthStore(state => state.saveToken);

    const validateOTPMutation = useMFAValidateOTPMutation();
    const recoveryMutation = useMFARecoveryMutation();

    const isLoading = validateOTPMutation.isPending || recoveryMutation.isPending;

    const resetWarningsAndErrors = useCallback(() => {
        setError(null);
        setWarning('');
    }, []);

    const setOTPError = (message: string) => {
        setError({
            status: 'error',
            message: 'The given data was invalid.',
            errors: { otp: [message] },
        });
    };

    const updateTokenAfterCodeVerification = (newToken: string, isRecovery = false) => {
        saveToken(newToken);
        if (isRecovery) {
            onRecoverySuccess?.();
        }
        onSuccess?.();
    };

    const validateConfigOTP = (code: string) => {
        resetWarningsAndErrors();

        // Validate with Zod
        const result = mfaOTPSchema.safeParse({ code });
        if (!result.success) {
            const errorMessage = result.error.errors[0]?.message || 'Invalid code format';
            setOTPError(errorMessage);
            return;
        }

        if (!loginEndpoint) {
            logWithSentry('[MFA-VALIDATE-OTP] - no-login-endpoint', {
                context: { error },
            });
            push('/login');
            return;
        }

        const endpoint = removePrefixIfExists(loginEndpoint, '/api/');
        const payload = {
            totp: result.data.code,
            ttl_days: MFA.DEFAULT_TTL_DAYS,
            ttl_start: Math.floor(Date.now() / 1000),
        };

        validateOTPMutation.mutate(
            { payload, endpoint, token },
            {
                onSuccess: response => {
                    updateTokenAfterCodeVerification(response.token);
                },
                onError: err => {
                    const apiError = handleApiError(err, 'Verification Failed');
                    if (apiError.status === 401) {
                        setOTPError('Session expired. Please log in again');
                    } else if (apiError.status === 400) {
                        setOTPError('Please enter a valid 6-digit code.');
                    } else {
                        setOTPError(apiError.message || 'Verification failed');
                    }
                },
            }
        );
    };

    const submitRecoveryText = (text: string) => {
        resetWarningsAndErrors();

        // Validate with Zod
        const result = mfaRecoverySchema.safeParse({ recoveryCode: text });
        if (!result.success) {
            const errorMessage = result.error.errors[0]?.message || 'Invalid recovery code';
            setOTPError(errorMessage);
            return;
        }

        const payload = {
            recovery_text: result.data.recoveryCode,
        };

        recoveryMutation.mutate(
            { payload, token },
            {
                onSuccess: response => {
                    // After successful recovery, trigger MFA reconfiguration
                    updateTokenAfterCodeVerification(response.token, true);
                },
                onError: err => {
                    const apiError = handleApiError(err, 'Recovery Failed');
                    if (apiError.status === 401) {
                        setOTPError('Session expired. Please log in again');
                    } else if (apiError.status === 400) {
                        setOTPError(apiError.message || 'Invalid recovery code');
                    } else {
                        setOTPError(apiError.message || 'Recovery failed');
                    }
                },
            }
        );
    };

    return {
        validateConfigOTP,
        submitRecoveryText,
        warning,
        error,
        resetWarningsAndErrors,
        isLoading,
    };
}
